import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  completeSubscriberChecklistStep,
  getIMQRequiredSubInfo,
  getIMQSubscriberById,
} from 'api/operations/electionService';
import { SubscriberDomainParams } from 'global/hooks/useSubscriberDomainParams';
import { getRootStoreInstance } from 'store/rootStore';
import EmployeeFlowDelegate from 'store/flows/subscriber/employeeFlowDelegate';
import { SubChecklistCard } from 'types/electionService';
import { ChecklistOption } from 'types/enrollmentService';
import SubscriberDependentDelegate from 'store/modules/elections/dependents/subDependentDelegate';
import SubscriberDelegate from 'store/modules/elections/subscriberDelegate';
import { AddDependentModalStore } from 'store/modules/elections/dependents/addDependentModalStore';
import { RemoveDependentModalStore } from 'store/modules/elections/dependents/removeDependentModalStore';
import { UpdateDependentModalStore } from 'store/modules/elections/dependents/updateDependentModalStore';

export default class IMQSubscriberDependentsPageStore {
  private readonly _rootStore = getRootStoreInstance();
  private readonly _domainParams: SubscriberDomainParams;
  private _manageDependentFormId = '';
  public readonly flowDelegate: EmployeeFlowDelegate;
  public subscriberDelegate: SubscriberDelegate | null = null;
  public addDependentModalStore: AddDependentModalStore | null = null;
  public removeDependentModalStore: RemoveDependentModalStore | null = null;
  public updateDependentModalStore: UpdateDependentModalStore | null = null;

  public get domainParams(): SubscriberDomainParams {
    return this._domainParams;
  }

  public get isLoading(): boolean {
    return this.flowDelegate.isInitialDataLoading;
  }

  public get checklistOptions(): ChecklistOption<SubChecklistCard>[] {
    return this.subscriberDelegate?.getTaskChecklistOptions(this._domainParams.entityId) || [];
  }

  public get formId(): string {
    return this._manageDependentFormId;
  }

  public get dependentDelegates(): SubscriberDependentDelegate[] {
    return this.subscriberDelegate?.subscriberDependentsDelegates || [];
  }

  private async _getSubscriber(): Promise<void> {
    const subscriber = await getIMQSubscriberById(this._domainParams.subscriberId);
    runInAction(() => {
      this.subscriberDelegate = new SubscriberDelegate({ subscriber, periodId: this._domainParams.entityId });
      this.addDependentModalStore = new AddDependentModalStore({
        subscriberDelegate: this.subscriberDelegate,
        formId: this._manageDependentFormId,
      });
    });
  }

  private async _getRequiredSubInfo(): Promise<void> {
    const domainId = this._rootStore.domainStoreV2.domainHeader.id;
    const requiredSubInfo = await getIMQRequiredSubInfo(domainId);
    runInAction(() => {
      this._manageDependentFormId = requiredSubInfo.imq.dependentFormId || '';
    });
  }

  public async getInitialData(): Promise<void> {
    this.flowDelegate.setIsInitialDataLoading(true);
    await this._getRequiredSubInfo();
    await this._getSubscriber();
    runInAction(() => {
      this.flowDelegate.setIsInitialDataLoading(false);
    });
  }

  public async completeChecklistStep(): Promise<void> {
    await completeSubscriberChecklistStep({
      subId: this._domainParams.subscriberId,
      entityId: this._domainParams.entityId,
      card: SubChecklistCard.IMQ_SUBSCRIBER_DEPENDENTS,
    });
    this.flowDelegate.onCompleteRedirect();
  }

  constructor(domainParams: SubscriberDomainParams) {
    this._domainParams = domainParams;
    this.flowDelegate = new EmployeeFlowDelegate({ domainParams });

    makeAutoObservable(
      this,
      {
        flowDelegate: false,
      },
      { autoBind: true },
    );
  }
}

const IMQSubscriberDependentsPageStoreContext = createContext(
  new IMQSubscriberDependentsPageStore({ groupId: '', subscriberId: '', entityId: '' }),
);

export const useIMQSubscriberDependentsPageStore = (): IMQSubscriberDependentsPageStore => {
  return useContext(IMQSubscriberDependentsPageStoreContext);
};

export { IMQSubscriberDependentsPageStoreContext };
